import { UserManager, User } from 'oidc-client';
import { IAuthenticationPort } from '../../ports/AuthenticationPort';
import {
  AuthenticatedUser,
  AccessToken,
  AuthenticationState,
  AuthenticationStateChangeEvent,
  AuthenticationError,
  AuthenticationErrorCode,
  LoginOptions,
  LogoutOptions,
  TokenRenewalOptions
} from '../../auth/models/AuthenticationModels';

/**
 * Base configuration shared by all OIDC authentication adapters
 */
export interface BaseAuthConfig {
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scopes: string[];
  silentRedirectUri?: string;
}

/**
 * Abstract base class for OIDC-based authentication adapters.
 * Provides shared implementation for common authentication operations.
 * 
 * Subclasses must implement:
 * - createUserManager(): Provider-specific UserManager configuration
 * - transformToDomainUser(): Provider-specific user profile mapping
 * - getProviderErrorMap(): Provider-specific error code mappings
 * - performSignIn(): Provider-specific sign-in logic
 * - performSignOut(): Provider-specific sign-out logic
 */
export abstract class BaseOidcAuthAdapter<TConfig extends BaseAuthConfig> implements IAuthenticationPort {
  protected readonly userManager: UserManager;
  protected readonly config: TConfig;
  protected currentState: AuthenticationState = AuthenticationState.UNAUTHENTICATED;
  protected stateChangeListeners: Array<(event: AuthenticationStateChangeEvent) => void> = [];
  protected currentUser: AuthenticatedUser | null = null;
  
  constructor(config: TConfig) {
    this.config = config;
    this.userManager = this.createUserManager(config);
    this.setupEventListeners();
    this.initializeState();
  }
  
  /**
   * Creates the OIDC UserManager with provider-specific configuration.
   * Must be implemented by subclasses.
   */
  protected abstract createUserManager(config: TConfig): UserManager;
  
  /**
   * Transforms an OIDC User object to the domain AuthenticatedUser model.
   * Must be implemented by subclasses for provider-specific profile mapping.
   */
  protected abstract transformToDomainUser(oidcUser: User): AuthenticatedUser;
  
  /**
   * Returns provider-specific error code mappings.
   * These are merged with base error mappings in mapErrorToDomain().
   */
  protected abstract getProviderErrorMap(): Record<string, AuthenticationErrorCode>;
  
  /**
   * Performs provider-specific sign-in redirect logic.
   */
  protected abstract performSignIn(options?: LoginOptions): Promise<void>;
  
  /**
   * Performs provider-specific sign-out logic.
   */
  protected abstract performSignOut(options?: LogoutOptions): Promise<void>;
  
  private setupEventListeners(): void {
    this.userManager.events.addUserLoaded(async (oidcUser) => {
      await this.updateStorageTokens(oidcUser.access_token);
      this.currentUser = this.transformToDomainUser(oidcUser);
      this.updateState(AuthenticationState.AUTHENTICATED, this.currentUser);
      this.userManager.clearStaleState();
    });
    
    this.userManager.events.addAccessTokenExpired(() => {
      this.currentUser = null;
      this.updateState(AuthenticationState.TOKEN_EXPIRED);
    });
    
    this.userManager.events.addAccessTokenExpiring(() => {
      this.updateState(AuthenticationState.REFRESHING_TOKEN, this.currentUser || undefined);
    });
    
    this.userManager.events.addSilentRenewError((error) => {
      this.updateState(AuthenticationState.TOKEN_EXPIRED, undefined, this.mapErrorToDomain(error));
    });
    
    this.userManager.events.addUserSignedOut(() => {
      this.currentUser = null;
      this.updateState(AuthenticationState.UNAUTHENTICATED);
    });
  }
  
  private async initializeState(): Promise<void> {
    try {
      const oidcUser = await this.userManager.getUser();
      if (oidcUser && !oidcUser.expired) {
        this.currentUser = this.transformToDomainUser(oidcUser);
        this.currentState = AuthenticationState.AUTHENTICATED;
      }
    } catch {
      this.currentState = AuthenticationState.UNAUTHENTICATED;
    }
  }
  
  protected updateState(newState: AuthenticationState, user?: AuthenticatedUser, error?: AuthenticationError): void {
    const previousState = this.currentState;
    this.currentState = newState;
    
    const event: AuthenticationStateChangeEvent = {
      previousState,
      currentState: newState,
      user,
      error,
      timestamp: new Date().toISOString()
    };
    
    this.stateChangeListeners.forEach(listener => listener(event));
  }
  
  protected transformToDomainToken(oidcUser: User): AccessToken {
    return {
      value: oidcUser.access_token,
      type: oidcUser.token_type || 'Bearer',
      expiresAt: new Date(oidcUser.expires_at * 1000),
      scopes: oidcUser.scopes || this.config.scopes
    };
  }
  
  protected mapErrorToDomain(error: any): AuthenticationError {
    if (error instanceof AuthenticationError) return error;
    
    const errorCode = error?.error || error?.code;
    const errorMessage = error?.message || error?.error_description || 'Unknown error';
    
    // Base error mappings common to all OIDC providers
    const baseErrorMap: Record<string, AuthenticationErrorCode> = {
      login_required: AuthenticationErrorCode.SESSION_EXPIRED,
      interaction_required: AuthenticationErrorCode.SESSION_EXPIRED,
      consent_required: AuthenticationErrorCode.CONSENT_REQUIRED,
      user_cancelled: AuthenticationErrorCode.USER_CANCELLED,
      invalid_grant: AuthenticationErrorCode.INVALID_CREDENTIALS,
      network_error: AuthenticationErrorCode.NETWORK_ERROR,
      timeout: AuthenticationErrorCode.NETWORK_ERROR
    };
    
    // Merge with provider-specific error mappings
    const errorMap = { ...baseErrorMap, ...this.getProviderErrorMap() };
    
    const code = errorMap[errorCode] || AuthenticationErrorCode.UNKNOWN_ERROR;
    const retryable = [AuthenticationErrorCode.NETWORK_ERROR, AuthenticationErrorCode.SERVICE_UNAVAILABLE].includes(code);
    
    return new AuthenticationError(code, errorMessage, retryable, { originalError: errorCode }, error);
  }
  
  // IAuthenticationPort implementation
  
  async getCurrentUser(): Promise<AuthenticatedUser | null> {
    if (this.currentUser) return this.currentUser;
    
    const oidcUser = await this.userManager.getUser();
    if (oidcUser && !oidcUser.expired) {
      this.currentUser = this.transformToDomainUser(oidcUser);
      return this.currentUser;
    }
    return null;
  }

  async updateStorageTokens(token: string): Promise<void> {
     localStorage.setItem('accessTokenFromProps', token);
     sessionStorage.setItem('accessTokenFromProps', token);
  }

  async clearStorageTokens(): Promise<void> {
     localStorage.removeItem('accessTokenFromProps');
     sessionStorage.removeItem('accessTokenFromProps');
  }
  
  async getAccessToken(_resource?: string, options?: TokenRenewalOptions): Promise<AccessToken> {
    let oidcUser = await this.userManager.getUser();
    
    if (!oidcUser || oidcUser.expired || options?.forceRenewal) {
      this.updateState(AuthenticationState.REFRESHING_TOKEN, this.currentUser || undefined);
      oidcUser = await this.userManager.signinSilent({ scope: options?.scopes?.join(' ') });
      
      if (!oidcUser) {
        throw new AuthenticationError(AuthenticationErrorCode.TOKEN_REFRESH_FAILED, 'Token renewal failed', false);
      }
      this.updateState(AuthenticationState.AUTHENTICATED, this.transformToDomainUser(oidcUser));
    }

    await this.updateStorageTokens(oidcUser.access_token);
    return this.transformToDomainToken(oidcUser);
  }
  
  async signIn(options?: LoginOptions): Promise<void> {
    this.updateState(AuthenticationState.AUTHENTICATING);
    try {
      await this.performSignIn(options);
    } catch (error) {
      const authError = this.mapErrorToDomain(error);
      this.updateState(AuthenticationState.UNAUTHENTICATED, undefined, authError);
      throw authError;
    }
  }
  
  async signOut(options?: LogoutOptions): Promise<void> {
    this.updateState(AuthenticationState.LOGGING_OUT, this.currentUser || undefined);
    
    // Always clear stale state to prevent orphaned tokens
    await this.userManager.clearStaleState();
    
    await this.performSignOut(options);
    await this.clearStorageTokens();
    
    this.currentUser = null;
    this.updateState(AuthenticationState.UNAUTHENTICATED);
  }
  
  onAuthStateChanged(callback: (event: AuthenticationStateChangeEvent) => void): () => void {
    this.stateChangeListeners.push(callback);
    return () => {
      this.stateChangeListeners = this.stateChangeListeners.filter(l => l !== callback);
    };
  }

  addUserLoaded(callback: (user: User) => void): void {
    this.userManager.events.addUserLoaded(callback);
  }

  removeUserLoaded(callback: (user: User) => void): void {
    this.userManager.events.removeUserLoaded(callback);
  }
  
  async isAuthenticated(): Promise<boolean> {
    const oidcUser = await this.userManager.getUser();
    return oidcUser !== null && !oidcUser.expired;
  }

  async signinSilent(): Promise<User> {
    return this.userManager.signinSilent();
  }
}
